import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { base } from '../base';
import { errorHandler } from '../utils';
import { talentProfile } from '../services/talentCalls';
import { companyProfile } from '../services/clientCalls';
import { claimAttribution } from '../services/attributionCalls';
import { getAttribution, isAttributionClaimed, markAttributionClaimed } from '../utils/attribution';
import { trackSignUp } from '../utils/analyticsEvents';

export const useOAuthCallbackHandler = (setLoad) => {
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);
  const oAuthToken = params.get("token");
  const oAuthError = params.get("error");
  const oAuthRole = params.get("role");
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("role");

  // Social logins talent
  const handleGoogleTalentLogin = () => {
    window.location.href = `${base}/api/auth/google?state=talent`;
  };
  const handleFacebookTalentLogin = () => {
    window.location.href = `${base}/api/auth/facebook?state=talent`;
  };
  const handleLinkedinTalentLogin = () => {
    window.location.href = `${base}/api/auth/linkedin?state=talent`;
  };

  // Social login client
  const handleGoogleClientLogin = () => {
    window.location.href = `${base}/api/auth/google?state=client`;
  };

  const handleFacebookClientLogin = () => {
    window.location.href = `${base}/api/auth/facebook?state=client`;
  };

  const handleLinkedinClientLogin = () => {
    window.location.href = `${base}/api/auth/linkedin?state=client`;
  };


  useEffect(() => {
    if (!oAuthToken && !oAuthError) return undefined;
    async function handleOAuthCallback() {
      try {
        setLoad(true);

        // Handle raw OAuth callback reaching the frontend
        if (oAuthError) {
          errorHandler(oAuthError || "Social authentication failed. Please try again.", "Login Error");
        }

        if (oAuthToken && oAuthRole) {
          localStorage.setItem("token", oAuthToken);
          localStorage.setItem("role", oAuthRole);
          const provider = params.get("provider");
          if (!isAttributionClaimed() && getAttribution()) {
            claimAttribution(getAttribution()).then(markAttributionClaimed).catch(() => {});
          }
          trackSignUp({
            method: provider || "social",
            userType: oAuthRole === "talent" ? "talent" : "client",
            email: oAuthToken.slice(-16),
          });

          if (oAuthRole === "talent") {
            const res = await talentProfile();
            if (res?.data?.data?.talent?.completedResumeUpload === "No") {
              navigate("/talent-onboarding?stage=1", { replace: true });
            } else if (res?.data?.data?.talent?.completedPersonalInformation === "No") {
              navigate("/talent-onboarding?stage=2", { replace: true });
            } else if (res?.data?.data?.talent?.completedEducation === "No") {
              navigate("/talent-onboarding?stage=3", { replace: true });
            } else if (res?.data?.data?.talent?.completedSkillsInformation === "No") {
              navigate("/talent-onboarding?stage=4", { replace: true });
            } else if (res?.data?.data?.talent?.completedWorkHistory === "No") {
              navigate("/talent-onboarding?stage=5", { replace: true });
            } else if (res?.data?.data?.talent?.completedWorkCompensation === "No") {
              navigate("/talent-onboarding?stage=6", { replace: true });
            } else {
              navigate("/self-development", { replace: true });
            }
          } else if (oAuthRole === "client") {
            const res = await companyProfile();
            if (res?.data?.data?.client?.completedLogoUpload === "No") {
              navigate("/client-onboarding?stage=1", { replace: true });
            } else if (res?.data?.data?.client?.completedCompanyType === "No") {
              navigate("/client-onboarding?stage=2", { replace: true });
            } else if (res?.data?.data?.client?.completedCompanyInformation === "No") {
              navigate("/client-onboarding?stage=3", { replace: true });
            } else if (res?.data?.data?.client?.completedAdditionalInfo === "No") {
              navigate("/client-onboarding?stage=4", { replace: true });
            } else {
              navigate("/client-jobs", { replace: true });
            }
          }
        } else if (token && userRole) {
          if (userRole === "talent") {
            navigate("/self-development");
          } else if (userRole === "client") {
            navigate("/client-jobs");
          }
        }
      } catch (error) {
        errorHandler(error, Object.values(error.errors || {})[0]);
      } finally {
        setLoad(false);
      }
    }
    handleOAuthCallback();
  }, [navigate, oAuthError, oAuthRole, oAuthToken, setLoad, token, userRole]);

  return {
    handleGoogleTalentLogin,
    handleFacebookTalentLogin,
    handleLinkedinTalentLogin,
    handleGoogleClientLogin,
    handleFacebookClientLogin,
    handleLinkedinClientLogin,
  };
};
